import { UpdatesNotepad } from "@/components/UpdatesNotepad";
import React from "react";
import { Outlet, useNavigate, Link } from "react-router-dom";
import { GroupedNav } from "@/components/nav/GroupedNav";
import { MobileTopBar, useMobileMenu } from "@/components/MobileTopBar";
import { useAuth } from "@/AuthContext";
import { LayoutDashboard, Calendar, PlaneTakeoff, Wallet, FileText, MessageSquare, LogOut, Euro, Quote, IdCard, LifeBuoy, Flag, Wrench, Ticket, BellRing, Building2, ScrollText, Timer, Stethoscope, Gavel, Utensils, Phone, Puzzle, Store, TreePine } from "lucide-react";
import { useActiveModules } from "@/hooks/useActiveModules";
import { moduleIcon } from "@/components/modules/moduleUtils";
import { LegalPendingBanner } from "@/components/legal/LegalPendingBanner";
import { LanguageCurrencySwitcher } from "@/components/LanguageCurrencySwitcher";
import { Button } from "@/components/ui/button";
import TestimonialDialog from "@/components/TestimonialDialog";
import { PushEnableBanner } from "@/components/PushNotifications";
import { PortalIncidentBanner } from "@/components/IncidentBanner";

/** Menu salarié groupé (Phase 74). */
const buildNav = ({ siteManager, pointage, talk, dynModules }) => ({
  top: [
    { to: "/salarie", label: "Mon espace", icon: LayoutDashboard, end: true },
    { to: "/salarie/ma-carte", label: "Ma carte ID", icon: IdCard },
    ...(siteManager ? [{ to: "/salarie/etablissement", label: "Mon établissement", icon: Building2 }] : []),
  ],
  groups: [
    { key: "temps", label: "Mon temps", icon: Calendar, items: [
      { to: "/salarie/planning", label: "Mon planning", icon: Calendar },
      ...(pointage ? [{ to: "/salarie/pointage", label: "Mon pointage", icon: Timer }] : []),
      ...(talk ? [{ to: "/salarie/talk", label: "Mon poste PAG Talk", icon: Phone }] : []),
      { to: "/salarie/conges", label: "Mes congés", icon: PlaneTakeoff },
    ] },
    { key: "paie", label: "Ma paie", icon: Euro, items: [
      { to: "/salarie/remuneration", label: "Ma rémunération", icon: Euro },
      { to: "/salarie/titres-restaurant", label: "Mes titres-restaurant", icon: Utensils },
      { to: "/salarie/paie", label: "Mes bulletins", icon: Wallet },
    ] },
    { key: "documents", label: "Mes documents", icon: FileText, items: [
      { to: "/salarie/documents", label: "Documents RH", icon: FileText },
      { to: "/salarie/contrat", label: "Mon contrat", icon: ScrollText },
      { to: "/salarie/visites-medicales", label: "Visites médicales", icon: Stethoscope },
      { to: "/salarie/legal", label: "Informations légales", icon: Gavel },
    ] },
    { key: "echanges", label: "Échanges & terrain", icon: MessageSquare, items: [
      { to: "/salarie/messages", label: "Messages", icon: MessageSquare },
      { to: "/salarie/notifications-reglages", label: "Mes notifications", icon: BellRing },
      { to: "/salarie/qualiparc/interventions", label: "Mes interventions", icon: Wrench },
      { to: "/salarie/qualiparc/tickets", label: "Mes tickets", icon: Ticket },
      { to: "/salarie/syndicat", label: "Syndicat", icon: Flag },
      { to: "/salarie/helper", label: "PAG Helper", icon: LifeBuoy },
    ] },
    { key: "marketplace", label: "Marketplace", icon: Store, items: [
      ...dynModules.map((m) => ({ to: `/salarie/modules/${m.key}`, label: m.name, icon: moduleIcon(m.icon) })),
      { to: "/salarie/marketplace", label: "Marketplace PBM", icon: Store },
    ] },
    { key: "billetterie", label: "Billetterie & Parcs", icon: TreePine, items: [
      { to: "/salarie/billetterie", label: "Mes billets & Parcs partenaires", icon: TreePine },
    ] },
  ],
});

export default function SalarieLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const mobile = useMobileMenu();
  const dynModules = useActiveModules();
  const { top, groups } = buildNav({ siteManager: !!user?.site_manager_of, pointage: (user?.entreprise?.options_active || []).includes("pointage_advanced"), talk: (user?.entreprise?.options_active || []).includes("pag_talk"), dynModules });

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="dashboard-shell bg-white">
      <MobileTopBar portal="salarie" label="Salarié" open={mobile.open} onToggle={mobile.toggle} />
      <aside className={`${mobile.asideClass} border-r border-[#DEE2E6] bg-white flex-col`} onClick={mobile.onNavClick} data-testid="salarie-sidebar">
        <div className="p-6 border-b border-[#DEE2E6] hidden lg:block">
          <Link to="/" className="flex items-center gap-3">
            <img src="/pag-logo.png" alt="PAG" className="h-9 w-9 object-contain" />
            <span className="font-display font-black text-lg">PAG</span>
            <span className="text-xs uppercase tracking-[0.25em] text-[#868E96] border-l border-[#DEE2E6] pl-3">Salarié</span>
          </Link>
        </div>
        <div className="p-4 border-b border-[#DEE2E6]">
          <div className="text-xs uppercase tracking-[0.2em] text-[#868E96] font-semibold">Entreprise</div>
          <div className="mt-1 font-display font-bold text-sm truncate">{user?.entreprise?.name || "—"}</div>
        </div>
        <GroupedNav portal="salarie" top={top} groups={groups} />
        <div className="p-4 border-t border-[#DEE2E6]">
          <div className="text-xs uppercase tracking-[0.2em] text-[#868E96] mb-1">Connecté</div>
          <div className="text-sm font-semibold">{user?.prenom} {user?.nom}</div>
          <div className="font-mono text-xs text-[#495057] mb-3">{user?.code}</div>
          <div className="mb-3"><LanguageCurrencySwitcher /></div>
          <Button
            onClick={doLogout}
            data-testid="salarie-logout-btn"
            variant="outline"
            className="w-full h-9 text-xs uppercase tracking-[0.2em] font-semibold rounded-md"
          >
            <LogOut className="h-3 w-3 mr-2" /> Déconnexion
          </Button>
          <TestimonialDialog
            trigger={
              <Button variant="ghost" data-testid="salarie-testimonial-btn" className="w-full mt-2 h-9 text-xs uppercase tracking-[0.2em] font-semibold rounded-md text-[#002FA7] hover:bg-[#002FA7]/5">
                <Quote className="h-3 w-3 mr-2" /> Laisser un avis
              </Button>
            }
          />
          <div className="mt-4 pt-4 border-t border-[#DEE2E6] text-[10px] text-[#868E96] space-y-0.5" data-testid="salarie-support">
            <div className="uppercase tracking-[0.2em] font-semibold mb-1">Support</div>
            <a href="mailto:contact@parcavenue.eu" className="block hover:text-[#002FA7] transition-colors truncate">contact@parcavenue.eu</a>
            <a href="tel:+33970701792" className="block hover:text-[#002FA7] transition-colors font-mono">09 70 70 17 92</a>
          </div>
        </div>
      </aside>

      <main className="overflow-y-auto bg-[#F8F9FA]">
        <PortalIncidentBanner />
        <PushEnableBanner />
        <LegalPendingBanner portal="salarie" />
        <UpdatesNotepad className="mx-6 lg:mx-10 mt-4" compact />
        <Outlet />
      </main>
    </div>
  );
}
